import React, { useState, useEffect } from 'react'
import { AsyncPaginate } from 'react-select-async-paginate'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { useFetchProducts } from '../hooks/useFetchProducts'
import { Table } from 'react-bootstrap'
import { formatMoney } from '../../../helpers/utils'
import dayjs from '../../../utils/dayjsConfig'

export default function Step3AssignProducts({
    baseBackendUrl,
    isClientSelected,
    title,
    onProductsChange,
    initialProducts = []
}) {
    const { fetchProducts } = useFetchProducts(baseBackendUrl)
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [selectedUnit, setSelectedUnit] = useState('')
    const [basePrice, setBasePrice] = useState(0)
    const [salePrice, setSalePrice] = useState('')
    const [productsTable, setProductsTable] = useState([])
    const [hasValidUnits, setHasValidUnits] = useState(true)
    const [duplicateWarning, setDuplicateWarning] = useState('')

    const loadProducts = async (inputValue, loadedOptions, { page }) => {
        const { options, hasMore } = await fetchProducts(inputValue, page)
        return {
            options,
            hasMore,
            additional: { page: page + 1 },
        }
    }

    const handleProductChange = (option) => {
        setDuplicateWarning('')

        if (!option) {
            setSelectedProduct(null)
            setSelectedUnit('')
            setBasePrice(0)
            setSalePrice('')
            setHasValidUnits(true)
            return
        }

        const product = option.product
        const packages = product?.packages || []
        const validPackages = packages.filter((p) => p.unit && p.unit.trim() !== '')

        if (validPackages.length === 0) {
            setHasValidUnits(false)
            setSelectedProduct(product)
            setSelectedUnit('')
            setBasePrice(0)
            setSalePrice('')
            return
        }

        const firstPackage = validPackages[0]
        setHasValidUnits(true)
        setSelectedProduct(product)
        setSelectedUnit(firstPackage.unit)
        setBasePrice(parseFloat(firstPackage.base_price || 0))
        setSalePrice('')
    }

    const handleAddProduct = () => {
        if (!selectedProduct || !selectedUnit) return

        const newItem = {
            db_ref: selectedProduct.db_ref,
            name: selectedProduct.name,
            unit: selectedUnit,
            base_price: parseFloat(basePrice || 0),
            price: parseFloat(salePrice || 0),
            created_at: selectedProduct.created_at || dayjs().toISOString()
        }

        const exists = productsTable.some(
            (p) => p.db_ref === newItem.db_ref && p.unit === newItem.unit
        )

        if (exists) {
            setDuplicateWarning(
                `El producto ${newItem.name} (${newItem.unit}) ya fue agregado. Si desea modificarlo, elimínelo de la tabla y vuelva a ingresarlo.`
            )
            return
        }

        const updatedTable = [...productsTable, newItem]
        setProductsTable(updatedTable)
        onProductsChange(updatedTable)

        setSelectedProduct(null)
        setSelectedUnit('')
        setBasePrice(0)
        setSalePrice('')
        setHasValidUnits(true)
        setDuplicateWarning('')
    }


    const handleDelete = (db_ref, unit) => {
        const updated = productsTable.filter((p) => !(p.db_ref === db_ref && p.unit === unit))
        setProductsTable(updated)
        onProductsChange(updated)
    }

    useEffect(() => {
        if (initialProducts.length > 0) {
            setProductsTable(initialProducts);
            onProductsChange(initialProducts);
        }
    }, [initialProducts]);


    const isDisabled = !isClientSelected || title.length <= 0
    const disableInputs = !hasValidUnits || !selectedProduct

    return (
        <>
            <div className="promo-form-section mt-20">
                <div className="title-promos-form">
                    <b className="bold-step">PASO 3: Escoja productos que aplican para esta lista de precios</b>
                </div>
                <hr className="hr-promos-form-price" />
            </div>

            <div className="promo-form-section">
                <div className="row g-2 align-items-end flex-nowrap">
                    <div className="col-5 col-lg-5 col-md-5">
                        <label>Producto</label>
                        <AsyncPaginate
                            placeholder="Buscar producto..."
                            debounceTimeout={400}
                            loadOptions={loadProducts}
                            additional={{ page: 1 }}
                            isDisabled={isDisabled}
                            isClearable
                            cacheOptions
                            menuPlacement="bottom"
                            menuPosition="fixed"
                            loadingMessage={() => 'Cargando...'}
                            noOptionsMessage={() => 'Sin resultados'}
                            value={selectedProduct ? { label: `${selectedProduct.db_ref} - ${selectedProduct.name}` } : null}
                            onChange={handleProductChange}
                        />
                    </div>
                    <div className="col-3 col-lg-3 col-md-3">
                        <label>Unidad de venta</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder='Unidad de venta'
                            disabled
                            value={selectedProduct?.packages?.find((p) => p.unit && p.unit.trim() !== '')?.unit || ''}
                        />
                    </div>
                    <div className="col-2 col-lg-2 col-md-2 custom-col">
                        <label>Precio unitario</label>
                        <input
                            type="text"
                            className="form-control"
                            disabled
                            placeholder="0.0"
                            value={selectedProduct && selectedUnit ? formatMoney(basePrice) : ''}
                        />
                    </div>

                    <div className="col-1 col-lg-1 col-md-1 custom-col">
                        <label>Precio venta</label>
                        <input
                            type="number"
                            className="form-control"
                            placeholder="0.0"
                            step="0.01"
                            min="0"
                            disabled={disableInputs || !selectedUnit}
                            value={salePrice}
                            onChange={(e) => setSalePrice(e.target.value)}
                        />
                    </div>

                    <div className="col-1 col-lg-1 col-md-1 d-flex align-items-end justify-content-end">
                        <button
                            className={`btnaddprice ${(!selectedUnit || !hasValidUnits) && 'inactive'}`}
                            style={{ minWidth: '100px', whiteSpace: 'nowrap' }}
                            disabled={!selectedProduct || !selectedUnit || !hasValidUnits}
                            type="button"
                            onClick={handleAddProduct}
                        >
                            <span className="icon-price-add">+ Agregar</span>
                        </button>
                    </div>
                </div>

                {!hasValidUnits && selectedProduct && (
                    <p className="warning-text mt-10">
                        El producto {selectedProduct.name} no tiene una unidad de venta. No es posible agregarlo.
                    </p>
                )}

                {duplicateWarning && (
                    <p className="warning-text mt-10">{duplicateWarning}</p>
                )}
            </div>

            {productsTable.length > 0 && (
                <div className="promo-form-section mt-20">
                    <Table responsive className="mt-33">
                        <thead className="table-header-gray">
                            <tr>
                                <th className='col-index'>#</th>
                                <th>CÓDIGO</th>
                                <th className='col-product'>PRODUCTO</th>
                                <th>UNIDAD DE VENTA</th>
                                <th>PRECIO UNITARIO</th>
                                <th>PRECIO VENTA</th>
                                <th>ELIMINAR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {productsTable.map((p, idx) => (
                                <tr key={`${p.db_ref}-${p.unit}-${idx}`}>
                                    <td className='col-index'>{idx + 1}</td>
                                    <td>{p.db_ref}</td>
                                    <td className="truncate-text" title={p.name}>
                                        {p.name}
                                    </td>
                                    <td>{p.unit}</td>
                                    <td>{formatMoney(p.base_price)}</td>
                                    <td>{formatMoney(p.price)}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className="delete-report-btn btn-delete-product"
                                            onClick={() => handleDelete(p.db_ref, p.unit)}
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} style={{ margin: 0 }} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}
        </>
    )
}
